// Admin shell — visual port of Cambonic_SaaS admin/AdminAppShell.tsx.
// Navy topbar with brand + nav + Admin badge + email + Sign out.
// Tailwind 3 arbitrary-syntax translation of Akrites' Tailwind 4 tokens.

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Boxes } from 'lucide-react';
import type { MeResponse } from '../../api/client.js';

interface AdminAppShellProps {
  me: MeResponse;
  children: React.ReactNode;
}

const navItems: Array<{ to: string; label: string; matches: (p: string) => boolean }> = [
  { to: '/', label: 'Back to app', matches: () => false },
  { to: '/admin/users', label: 'Users', matches: (p) => p.startsWith('/admin/users') },
  { to: '/admin/analytics', label: 'Analytics', matches: (p) => p.startsWith('/admin/analytics') },
  { to: '/admin/storage', label: 'Storage', matches: (p) => p.startsWith('/admin/storage') },
];

export function AdminAppShell({ me, children }: AdminAppShellProps) {
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      window.location.replace('/');
    }
  }

  return (
    <main className="min-h-screen bg-[rgb(var(--color-linen))]">
      {/* Topbar — Akrites pattern.
          Geometry contract:
          - Outer row: h-14, flex items-center, gap-6
          - Every child of the inner clusters: h-7 + flex items-center + leading-none
            so the centerlines all match. */}
      <header className="sticky top-0 z-30 bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))] border-b border-[rgb(var(--color-navy-light)/0.3)]">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between gap-6">
          {/* Left cluster */}
          <div className="flex items-center gap-6 min-w-0 h-14">
            <Link to="/admin" className="flex items-center gap-2.5 shrink-0 h-7 leading-none">
              <Boxes size={20} className="shrink-0" />
              <span className="font-display text-[17px] font-semibold tracking-tight">
                GutterInv
              </span>
            </Link>
            <nav className="flex items-stretch gap-1 h-14">
              {navItems.map((item) => {
                const active = item.matches(location.pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`relative flex items-center px-3 text-sm leading-none transition-colors h-14 ${
                      active
                        ? 'text-[rgb(var(--color-linen))] font-semibold'
                        : 'text-[rgb(var(--color-linen)/0.65)] hover:text-[rgb(var(--color-linen))]'
                    }`}
                  >
                    {item.label}
                    {active && (
                      <span className="absolute left-2 right-2 bottom-0 h-[3px] bg-[#C49659] rounded-t-[2px] shadow-[0_-1px_4px_rgba(196,150,89,0.4)]" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right cluster — grid layout, place-items-center per child for
              exact-center across both axes. */}
          <div className="grid grid-flow-col auto-cols-max items-center gap-3 shrink-0 h-14">
            <span
              className="grid place-items-center h-7 px-3 rounded-md bg-[rgb(var(--color-bronze))] text-white text-[11px] font-extrabold tracking-[0.18em] uppercase leading-none ring-1 ring-[rgb(var(--color-bronze)/0.7)] shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
              aria-label="Admin view"
            >
              Admin
            </span>
            <span
              className="hidden sm:grid place-items-center h-7 text-[rgb(var(--color-linen)/0.75)] text-sm leading-none"
              title={me.email}
            >
              {me.displayName ?? me.email}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              disabled={loggingOut}
              className="grid place-items-center h-7 px-2 rounded-[var(--radius-sm)] text-[rgb(var(--color-linen)/0.7)] hover:text-[rgb(var(--color-linen))] text-xs font-medium leading-none hover:bg-[rgb(var(--color-linen)/0.05)] transition-colors"
            >
              {loggingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </header>

      {children}
    </main>
  );
}
